/** Данные и чистые помощники панели генерации: шаги, ход, тело запроса, ассеты, опросы. */
import type { GenerationJobTelemetry } from "@/lib/generation-phase"

/** Шаг генерации в полоске из шести делений. */
export const GENERATION_STEP_KEYS = ["queue", "refs", "submit", "provider", "download", "save"] as const
export type GenerationStepKey = (typeof GENERATION_STEP_KEYS)[number]

const PHASE_STEP: Record<string, number> = {
  queued: 0,
  claimed: 0,
  preparing_refs: 1,
  submitting: 2,
  in_flight: 2,
  provider_queued: 3,
  provider_running: 3,
  parked: 3,
  downloading: 4,
  attaching: 5,
}

export type GenerationStepState = "done" | "now" | "warn" | "todo"

/** Номер текущего шага и тревога; stalled ставится на шаг фазы, из которой ушли в stalled. */
export function phaseStep(
  phase: string | null | undefined,
  meta?: Record<string, unknown> | null,
): { index: number; warn: boolean } | null {
  if (!phase) return null
  if (phase === "stalled") {
    const from = typeof meta?.stalled_from === "string" ? meta.stalled_from : null
    const index = from != null && from in PHASE_STEP ? PHASE_STEP[from] : 0
    return { index, warn: true }
  }
  // Продолжение опроса после слайса: задача у провайдера, полоска не откатывается к «Очереди».
  if (meta?.resume === true && (phase === "queued" || phase === "claimed"))
    return { index: PHASE_STEP.parked, warn: false }
  if (phase in PHASE_STEP) return { index: PHASE_STEP[phase], warn: false }
  return null
}

export function stepStates(telemetry: GenerationJobTelemetry | null | undefined): GenerationStepState[] | null {
  const step = phaseStep(telemetry?.phase, telemetry?.phase_meta)
  if (!step) return null
  return GENERATION_STEP_KEYS.map((_key, i) =>
    i < step.index ? "done" : i === step.index ? (step.warn ? "warn" : "now") : "todo",
  )
}

/** Событие «Хода генерации» (generation_job_events). */
export type GenerationJobEvent = {
  phase: string
  started_at: string
  duration_ms: number | null
  polls: number
  meta: Record<string, unknown> | null
}

/** Строка «Хода генерации» после схлопывания: попытки запроса и отпуски воркера. */
export type GenerationTimelineRow = GenerationJobEvent & { attempts: number; resumes: number }

const PROVIDER_WAIT_PHASES = new Set(["in_flight", "provider_queued", "provider_running", "parked"])
const REQUEST_PHASES = new Set(["preparing_refs", "submitting", "in_flight"])

/**
 * Ход генерации без технического шума.
 * — Слайсы опроса (до 4 ноября 2026 писали «очередь → воркер взял задачу → генерация» каждые 90 с):
 *   queued/claimed после фазы провайдера и parked сливаются в фазу провайдера, считаются как resumes.
 * — Повтор запроса (подготовка → запрос → задача принята → снова подготовка → запрос):
 *   неудачные попытки сливаются в одну строку «запрос к провайдеру» с attempts.
 * — Подряд идущие одинаковые фазы — одна строка: длительности и проверки складываются.
 */
export function compactTimeline(events: GenerationJobEvent[]): GenerationTimelineRow[] {
  const rows: GenerationTimelineRow[] = []
  const endOf = (event: GenerationJobEvent): number | null =>
    event.duration_ms == null ? null : Date.parse(event.started_at) + event.duration_ms
  const extend = (row: GenerationTimelineRow, event: GenerationJobEvent) => {
    const end = endOf(event)
    row.duration_ms = end == null ? null : end - Date.parse(row.started_at)
    row.polls += event.polls
  }
  // Запрос, после которого опрос ещё не начался: следующая попытка сливается в него.
  let openRequest: GenerationTimelineRow | null = null

  events.forEach((event, i) => {
    const last = rows[rows.length - 1]
    const next = events[i + 1]
    const inProviderWait = last != null && PROVIDER_WAIT_PHASES.has(last.phase) && last.phase !== "in_flight"

    if (inProviderWait && (event.phase === "queued" || event.phase === "claimed" || event.phase === "parked")) {
      if (event.phase === "claimed") last.resumes += 1
      extend(last, event)
      return
    }
    if (openRequest && REQUEST_PHASES.has(event.phase)) {
      const retry = event.phase === "submitting"
      // «задача принята» без опроса, за которой снова запрос, — это неудачная попытка.
      const failedAccept = event.phase === "in_flight" && next != null && REQUEST_PHASES.has(next.phase)
      if (retry || event.phase === "preparing_refs" || failedAccept) {
        if (retry) openRequest.attempts += 1
        extend(last, event)
        return
      }
    }
    if (last && last.phase === event.phase) {
      extend(last, event)
      return
    }

    const row: GenerationTimelineRow = { ...event, attempts: event.phase === "submitting" ? 1 : 0, resumes: 0 }
    rows.push(row)
    if (event.phase === "submitting") openRequest = row
    else if (!REQUEST_PHASES.has(event.phase)) openRequest = null
  })
  return rows
}

/** Ассет, ушедший в конкретной попытке запроса (generation_request_assets). */
export type RequestLogAsset = {
  exchange_index: number
  json_path: string
  role: string | null
  delivery: "tos" | "direct" | "asset" | "inline" | "external"
  url: string | null
  filename: string | null
  content_type: string | null
  byte_size: number | null
  owner_label: string | null
  external_ref: string | null
}

/** Строка журнала запросов для панели генерации. */
export type GenerationDetailLog = {
  id: number
  provider: string
  ai_model: string | null
  api_endpoint: string | null
  status: string
  request_payload: unknown
  response_payload: unknown
  error_message: string | null
  duration_ms: number | null
  created_at: string
  external_task_id: string | null
  loggable_id?: number | null
  assets?: RequestLogAsset[]
}

/** Ответ request_logs-эндпоинтов. */
export type GenerationJobDetailData = {
  logs: GenerationDetailLog[]
  telemetry?: GenerationJobTelemetry | null
  events?: GenerationJobEvent[]
  events_synthetic?: boolean
}

type JsonRecord = Record<string, unknown>

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === "object" && value != null && !Array.isArray(value)
}

/** Обычный (не poll) обмен журнала: verb, url, тело запроса, ответ. */
export type PlainExchange = {
  index: number
  verb: string
  url: string
  request: unknown
  response: unknown
}

/** Опрос: статус, сколько раз подряд, первый и последний момент. */
export type PollGroup = {
  index: number
  status: string
  count: number
  lastPolledAt: string | null
}

export function logExchanges(payload: unknown): unknown[] {
  if (!isRecord(payload)) return []
  return Array.isArray(payload.exchanges) ? payload.exchanges : []
}

export function plainExchanges(payload: unknown): PlainExchange[] {
  return logExchanges(payload).flatMap((ex, index) => {
    if (!isRecord(ex) || ex.kind === "poll") return []
    return [
      {
        index,
        verb: typeof ex.verb === "string" ? ex.verb : "",
        url: typeof ex.url === "string" ? ex.url : "",
        request: ex.request,
        response: ex.response,
      },
    ]
  })
}

/** Отправленные тела (POST и т.п.) — попытки запроса; GET без тела сюда не входят. */
export function requestAttempts(payload: unknown, fallbackBody: unknown): PlainExchange[] {
  const attempts = plainExchanges(payload).filter((ex) => ex.verb !== "GET" && ex.request != null)
  if (attempts.length > 0) return attempts
  if (fallbackBody != null && isRecord(fallbackBody) && fallbackBody.pending !== true) {
    return [{ index: 0, verb: "POST", url: "", request: fallbackBody, response: null }]
  }
  return []
}

export function pollGroups(payload: unknown): PollGroup[] {
  return logExchanges(payload).flatMap((ex, index) => {
    if (!isRecord(ex) || ex.kind !== "poll") return []
    return [
      {
        index,
        status: typeof ex.task_status === "string" && ex.task_status ? ex.task_status : "?",
        count: (typeof ex.repeat_count === "number" ? ex.repeat_count : 0) + 1,
        lastPolledAt: typeof ex.last_polled_at === "string" && ex.last_polled_at ? ex.last_polled_at : null,
      },
    ]
  })
}

export function pollCount(payload: unknown): number {
  return pollGroups(payload).reduce((sum, group) => sum + group.count, 0)
}

function textOfContent(content: unknown): string | null {
  if (typeof content === "string") return content
  if (!Array.isArray(content)) return null
  const texts = content.flatMap((part) => {
    if (!isRecord(part)) return []
    if (typeof part.text === "string" && (part.type == null || part.type === "text")) return [part.text]
    return []
  })
  return texts.length > 0 ? texts.join("\n\n") : null
}

/** Итоговый промпт из тела запроса — в форматах провайдеров, которые мы вызываем. */
export function extractRequestPrompt(body: unknown): string | null {
  if (!isRecord(body)) return null
  if (typeof body.prompt === "string" && body.prompt.trim()) return body.prompt
  if (isRecord(body.input) && typeof body.input.prompt === "string" && body.input.prompt.trim()) {
    return body.input.prompt
  }
  const fromContent = textOfContent(body.content)
  if (fromContent) return fromContent
  if (Array.isArray(body.contents)) {
    const texts = body.contents.flatMap((c) => (isRecord(c) ? [textOfContent(c.parts)] : [])).filter(Boolean)
    if (texts.length > 0) return texts.join("\n\n")
  }
  if (Array.isArray(body.messages)) {
    const users = body.messages.filter((m) => isRecord(m) && m.role === "user")
    const last = users[users.length - 1]
    if (isRecord(last)) return textOfContent(last.content)
  }
  return null
}

const PROMPT_KEYS = new Set(["prompt", "negative_prompt_text", "text", "content", "contents", "messages"])
const NESTED_SETTING_KEYS = ["input", "parameters", "generationConfig", "imageConfig", "config"]
const MAX_SETTING_LENGTH = 120

function settingValue(value: unknown): string | null {
  if (typeof value === "number" || typeof value === "boolean") return String(value)
  if (typeof value === "string") {
    if (value.length > MAX_SETTING_LENGTH || /^(https?:|data:|asset:)/i.test(value)) return null
    return value
  }
  return null
}

/** Скалярные настройки запроса: модель, разрешение, длительность, seed… — без промпта и ссылок. */
export function extractRequestSettings(body: unknown): [string, string][] {
  if (!isRecord(body)) return []
  const out: [string, string][] = []
  const visit = (record: JsonRecord, prefix: string) => {
    for (const [key, value] of Object.entries(record)) {
      if (PROMPT_KEYS.has(key)) continue
      if (isRecord(value) && NESTED_SETTING_KEYS.includes(key)) {
        visit(value, `${prefix}${key}.`)
        continue
      }
      const shown = settingValue(value)
      if (shown != null) out.push([`${prefix}${key}`, shown])
    }
  }
  visit(body, "")
  return out
}

export function assetsForExchange(assets: RequestLogAsset[] | undefined, exchangeIndex: number): RequestLogAsset[] {
  return (assets ?? []).filter((asset) => asset.exchange_index === exchangeIndex)
}

/** «12:21:04» в локальном времени. */
export function formatClockTime(iso: string | null | undefined, locale = "ru-RU"): string {
  if (!iso) return ""
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return ""
  return at.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", second: "2-digit" })
}

/** Заголовок дня для ленты: «Сегодня», «Вчера» или дата. */
export function dayKey(iso: string): string {
  const at = new Date(iso)
  return `${at.getFullYear()}-${at.getMonth()}-${at.getDate()}`
}

export function dayOffset(iso: string, now: number): number {
  const at = new Date(iso)
  const today = new Date(now)
  const a = Date.UTC(at.getFullYear(), at.getMonth(), at.getDate())
  const b = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
  return Math.round((b - a) / 86_400_000)
}
